import {Controller} from "@hotwired/stimulus"

// Credit price on a return. The cell stays read-only at what the line sold for, so
// the common case — credit what they paid — needs no typing and cannot be
// fat-fingered. Reducing it is the exception (damaged goods, a return outside the
// window) and happens in a popover, so the row never changes height while editing.
export default class extends Controller {
    static targets = ["input", "amount", "panel", "reset"]
    static values = {sold: Number}

    connect() {
        this.render()

        this.closeOnOutside = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }
        this.closeOnEscape = (event) => {
            if (event.key === "Escape") this.close()
        }
    }

    disconnect() {
        this.unbind()
    }

    toggle(event) {
        event.preventDefault()
        this.panelTarget.hidden ? this.open() : this.close()
    }

    open() {
        this.panelTarget.hidden = false
        this.inputTarget.focus()
        this.inputTarget.select()

        // Deferred: the click that opened the panel is still travelling, and binding
        // now would close it again on the way up.
        setTimeout(() => {
            document.addEventListener("click", this.closeOnOutside)
            document.addEventListener("keydown", this.closeOnEscape)
        })
    }

    close() {
        if (this.panelTarget.hidden) return

        this.clamp()
        this.panelTarget.hidden = true
        this.unbind()
    }

    unbind() {
        document.removeEventListener("click", this.closeOnOutside)
        document.removeEventListener("keydown", this.closeOnEscape)
    }

    // Enter inside the table would submit the order — the form has a submit button,
    // so a keypress in a lone field counts as implicit submission.
    closeOnEnter(event) {
        event.preventDefault()
        this.close()
    }

    reset(event) {
        event.preventDefault()
        this.commit(this.soldValue)
    }

    // Only a blank or negative field is repaired here, on the way out. Going down is
    // legitimate, so half-typed values are left alone while the panel is open.
    clamp() {
        const value = parseFloat(this.inputTarget.value)

        if (isNaN(value) || value < 0) this.commit(this.soldValue)

        this.render()
    }

    render() {
        const value = parseFloat(this.inputTarget.value)

        // Going up is caught as it is typed, not on the way out: there is no such
        // thing as a legitimate intermediate value above what the line sold for, and
        // an extra digit at the end is the whole reason this cell is guarded.
        if (!isNaN(value) && value > this.soldValue) return this.commit(this.soldValue)

        const amount = isNaN(value) ? 0 : value
        const reduced = amount < this.soldValue

        this.amountTarget.textContent = this.currency(amount)
        this.amountTarget.classList.toggle("ice:text-warning-700", reduced)
        this.amountTarget.classList.toggle("ice:text-slate-900", !reduced)

        // Disabled rather than hidden: appearing mid-edit would shift the field it
        // sits next to.
        if (this.hasResetTarget) this.resetTarget.disabled = !reduced
    }

    commit(amount) {
        this.inputTarget.value = amount.toFixed(2)
        // Keeps the row total and the order total in step — they listen for keyup.
        this.inputTarget.dispatchEvent(new Event("keyup", {bubbles: true}))
        this.render()
    }

    currency(number) {
        return number.toLocaleString("en-US", {style: "currency", currency: "USD"})
    }
}
